import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Newspaper, Bell, MapPin } from 'lucide-react';
import DisasterNewsModal from './DisasterNewsModal';
import { useLocationDetection } from '../../hooks/useLocationDetection';

const NewsButton: React.FC = () => {
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);
  const { location, isLoading, detectLocation } = useLocationDetection();

  // Auto-detect location on component mount
  useEffect(() => {
    if (!location && !isLoading) {
      detectLocation();
    }
  }, [location, isLoading, detectLocation]);

  const getLocationText = () => {
    if (isLoading) return 'Detecting...';
    if (!location) return 'Global';
    
    // Show city first, then region, then country
    if (location.city) return location.city;
    if (location.region) return location.region;
    return location.country;
  };

  const getLocationFlag = () => {
    if (!location?.countryCode) return '🌍';
    
    // Convert country code to flag emoji
    const countryCodeToFlag = (countryCode: string) => {
      return countryCode
        .toUpperCase()
        .replace(/./g, char => String.fromCodePoint(127397 + char.charCodeAt(0)));
    };
    
    return countryCodeToFlag(location.countryCode);
  };

  return (
    <>
      {/* Floating News Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsNewsModalOpen(true)}
        className="fixed bottom-6 left-6 bg-gradient-to-r from-accent-red to-accent-orange rounded-2xl shadow-lg z-40 group overflow-hidden"
        title={`Local Disaster News - ${getLocationText()}`}
      >
        <div className="relative p-4">
          {/* Main content */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Newspaper className="w-6 h-6 text-white" />
              {/* Notification indicator */}
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute -top-1 -right-1 w-3 h-3 bg-accent-yellow rounded-full"
              />
            </div>
            
            {/* Location info */}
            <div className="text-left">
              <div className="text-white font-semibold text-sm">Local News</div>
              <div className="flex items-center space-x-1 text-white/80 text-xs">
                <span className="text-sm">{getLocationFlag()}</span>
                <span className="max-w-20 truncate">{getLocationText()}</span>
                {isLoading && (
                  <div className="w-3 h-3 border border-white/50 border-t-white rounded-full animate-spin ml-1" />
                )}
              </div>
            </div>
          </div>
          
          {/* Hover effect overlay */}
          <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
        </div>
        
        {/* Tooltip */}
        <div className="absolute left-full ml-3 px-3 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <div className="font-medium">Local Disaster News</div>
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {getLocationText()}
          </div>
          <div className="absolute left-0 top-1/2 transform -translate-x-1 -translate-y-1/2 w-2 h-2 bg-dark-900 border-l border-b border-dark-600 rotate-45"></div>
        </div>
      </motion.button>

      {/* News Modal with location data */}
      <DisasterNewsModal
        isOpen={isNewsModalOpen}
        onClose={() => setIsNewsModalOpen(false)}
        userLocation={location}
      />
    </>
  );
};

export default NewsButton;